import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Box, CircularProgress, Typography, Button, Alert, Stack } from '@mui/material'
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material'
import { ContentContainer, Divider, SplitHalf, WorkforceSection, WrapperStyled, useAuth, track, eventTypes } from 'torqcloud-ui'
import { appConfig } from '@/config'
import useApiClient from '@/hooks/useApiClient'

interface JoinOrganizationResponse {
  organization: {
    id: string
    name: string
    display_name: string
  }
  role: string
  created: boolean
}

interface ApiError {
  response?: {
    data?: {
      error?: string
      message?: string
    }
  }
  message?: string
}

type ViewState = 'loading' | 'success' | 'error' | 'timeout'

const TIMEOUT_MS = 30000 // 30 seconds
const SUCCESS_DISPLAY_MS = 1500 // Show success for 1.5s before redirect

export default function JoinOrganizationPage() {
  const navigate = useNavigate()
  const { user, refreshMetadata } = useAuth()
  const { torqCloudClient } = useApiClient()

  const [viewState, setViewState] = useState<ViewState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [organizationName, setOrganizationName] = useState<string>('')

  useEffect(() => {
    // Ensure user is authenticated
    if (!user) {
      console.error('User not authenticated, redirecting to login')
      navigate('/login')
      return
    }

    joinOrganization()

    // Set timeout
    const timeoutId = setTimeout(() => {
      if (viewState === 'loading') {
        setViewState('timeout')
        setErrorMessage('Request timed out. Please try again.')
      }
    }, TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run once on mount

  const joinOrganization = async () => {
    setViewState('loading')
    setErrorMessage('')
    track(eventTypes.ORGANIZATION_JOIN_STARTED)

    try {
      const response = await torqCloudClient.post<JoinOrganizationResponse>('/v1/users/join-organization/')

      const { organization } = response.data

      // Store organization name for success message
      const resolvedOrganizationName = organization.display_name || organization.name
      setOrganizationName(resolvedOrganizationName)
      track(eventTypes.ORGANIZATION_JOINED, { organizationName: resolvedOrganizationName, alreadyMember: false })

      // Refresh auth metadata so OrganizationStatusGuard has the new org data
      try {
        await refreshMetadata()
      } catch (err) {
        console.error('Failed to refresh metadata after joining org:', err)
      }

      // Show success state briefly, then redirect
      setViewState('success')
      setTimeout(() => {
        navigate('/playground')
      }, SUCCESS_DISPLAY_MS)
    } catch (error) {
      console.error('Error joining organization:', error)
      handleError(error as ApiError)
    }
  }

  const handleError = (error: ApiError) => {
    const errorCode = error.response?.data?.error
    const errorMsg = error.response?.data?.message

    switch (errorCode) {
      case 'email_not_verified':
        // This shouldn't happen, but handle defensively
        setErrorMessage('Your email must be verified before joining.')
        setViewState('error')
        // Redirect to verification page
        setTimeout(() => {
          navigate('/verify-email')
        }, 2000)
        break

      case 'no_organization_found':
        // Redirect to payment portal
        const paymentPortalUrl = appConfig.paymentPortalUrl || ''
        if (paymentPortalUrl) {
          window.location.href = paymentPortalUrl
        } else {
          console.error('Payment portal URL not configured')
          setErrorMessage('No organization found for your email domain. Please contact support.')
          setViewState('error')
        }
        break

      case 'already_member':
        // Treat as success - user is already a member
        track(eventTypes.ORGANIZATION_JOINED, { organizationName, alreadyMember: true })
        refreshMetadata().catch(err => console.error('Failed to refresh metadata:', err))
        setViewState('success')
        setTimeout(() => {
          navigate('/playground')
        }, SUCCESS_DISPLAY_MS)
        break

      default:
        // Generic error
        track(eventTypes.ORGANIZATION_JOIN_FAILED, { reason: errorCode || 'unknown' })
        setErrorMessage(errorMsg || 'Unable to join organization. Please try again.')
        setViewState('error')
    }
  }

  const handleRetry = () => {
    joinOrganization()
  }

  return (
    <WrapperStyled>
      <Helmet>
        <title>{`Join Organization | ${appConfig.APP_NAME}`}</title>
      </Helmet>
      <Box style={{ ...ContentContainer }}>
        <WorkforceSection />
        <Divider />
        <Box sx={{ ...SplitHalf, right: 0, margin: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: 3,
            }}
          >
            <Stack spacing={3} sx={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
              {/* Loading State */}
              {viewState === 'loading' && (
                <>
                  <CircularProgress size={60} sx={{ alignSelf: 'center' }} />
                  <Typography variant="h5" gutterBottom>
                    Setting up your account...
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Adding you to your organization
                  </Typography>
                </>
              )}

              {/* Success State */}
              {viewState === 'success' && (
                <>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 80,
                      color: 'success.main',
                      alignSelf: 'center',
                    }}
                  />
                  <Typography variant="h5" gutterBottom>
                    Welcome!
                  </Typography>
                  {organizationName && (
                    <Typography variant="body1" color="text.secondary">
                      You've been added to {organizationName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Redirecting...
                  </Typography>
                </>
              )}

              {/* Error State */}
              {(viewState === 'error' || viewState === 'timeout') && (
                <>
                  <ErrorIcon
                    sx={{
                      fontSize: 80,
                      color: 'error.main',
                      alignSelf: 'center',
                    }}
                  />
                  <Typography variant="h5" gutterBottom>
                    Unable to Join Organization
                  </Typography>

                  <Alert severity="error">{errorMessage}</Alert>

                  <Button variant="contained" size="large" onClick={handleRetry} fullWidth>
                    Try Again
                  </Button>

                  <Typography variant="caption" color="text.secondary">
                    If this problem persists, please contact support
                  </Typography>
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </WrapperStyled>
  )
}
